import { Helmet } from "react-helmet";
import { useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useParams, useHistory } from "react-router";
import { useForm, FormProvider } from "react-hook-form";
import Layout from "../../../../components/Layout";
import userPlaceholder from "../../../../assets/user-placeholder.svg";
import { Heading, Text } from "../../../../components/Type";
import Button from "../../../../components/Button";
import { ArrowRight } from "../../../../components/Icons/general-interface-1";
import CareTeamSidebar from "../../../../components/CareTeamSidebar";
import { useStoreContext } from "../../../../contexts/StoreProvider";
import Modal from "../../../../components/Modal";
import { UserType } from "../../../../types/userType";
import ClientForm from "../../../../components/Client/Edit/";
import styles from "./index.module.scss";
import { sanitizeUpdateClientData } from "../../../../utils/forms/sanitization";

const GET_CLIENT = gql`
  query Client($id: ID!) {
    client(id: $id) {
      id
      first_name
      last_name
      social_assistance
      health_card
      health_card_version
      birth_date
      long_term_care_home
      sex
      cep
      address {
        address_1
        address_2
        unit_number
        street_number
        street_name
        lot
        business_phone
        business_phone_ext
        postal_code
        city
        province
        phone
      }
      agent {
        unit_number
        street_number
        street_name
        lot
        business_phone
        business_phone_ext
        postal_code
        city
        province
        phone
        first_name
        last_name
        middle_initial
        relationship
      }
      authorizers {
        id
        first_name
        last_name
        role {
          type
          name
        }
      }
      sales_reps {
        id
        first_name
        last_name
        role {
          type
          name
        }
      }
      client_files {
        id
        status
        quote {
          url
        }
        client_form {
          id
          updated_at
        }
      }
    }
  }
`;

const UPDATE_CLIENT = gql`
  mutation UpdateClient($input: updateClientInput) {
    updateClient(input: $input) {
      client {
        id
      }
    }
  }
`;

const EditClient = () => {
  const {
    state: { user },
  } = useStoreContext();
  const params = useParams<{ client_id: string }>();
  const history = useHistory();
  const methods = useForm({
    mode: "onTouched",
  });
  const [careTeam, setCareTeam] = useState<UserType[]>([]);
  const [, setBoundaryError] = useState<Error | null>(null);
  const { data, error, loading } = useQuery(GET_CLIENT, {
    variables: {
      id: params.client_id,
    },
    fetchPolicy: "no-cache",
    skip: !params || !params.client_id,
    onCompleted: (data) => {
      setCareTeam([...data.client.sales_reps, ...data.client.authorizers]);
      const {
        first_name,
        last_name,
        middle_name,
        birth_date,
        sex,
        health_card,
        health_card_version,
        long_term_care_home,
        social_assistance,
        address,
        agent,
      } = data.client;
      const sanitizedData = {
        first_name,
        last_name,
        middle_name,
        birth_date,
        sex,
        health_card,
        health_card_version,
        long_term_care_home,
        social_assistance,
        address,
        agent,
        cep: data.client.cep ? "true" : "false",
        social_benefits: data.client.social_assistance ? "true" : "false",
      };
      methods.reset(sanitizedData);
    },
  });

  const [updateClient, { client }] = useMutation(UPDATE_CLIENT, {
    onCompleted: (data) => {
      client.resetStore();
      history.push(`/client/${data.updateClient.client.id}`);
    },
  });

  const [openUploadModal, setOpenUploadModal] = useState(false);

  const handleSubmit = (values) => {
    updateClient({
      variables: {
        input: {
          data: sanitizeUpdateClientData(values),
          where: {
            id: data.client.id,
          },
        },
      },
    }).catch((error) => {
      setBoundaryError(() => {
        throw error;
      });
    });
  };

  const handleSidebarSave = (users) => {
    methods.setValue(
      "sales_reps",
      users.filter((u) => u.role.type === "sales_rep").map((u) => u.id)
    );
    methods.setValue(
      "authorizers",
      users.filter((u) => ["authorizer", "motion_support"].includes(u.role.type)).map((u) => u.id)
    );
  };

  const benefitsSelection = methods.watch("social_benefits", "false");

  return (
    <FormProvider {...methods}>
      <Layout>
        <Helmet>
          <title>Edit Client</title>
        </Helmet>
        <div className={styles.content}>
          {error && error.message}
          {loading ? null : (
            <>
              <div className={styles.profileHeader}>
                <div className={styles.profileImage}>
                  <img src={userPlaceholder} alt="f" />
                </div>
                <div className={styles.profileContent}>
                  <div className={styles.profileContentWrap}>
                    <Heading size="h3" className={styles.profileName}>
                      {`${data?.client.first_name} ${data?.client.last_name}`}
                    </Heading>
                    <Text
                      size="small"
                      lineHeight="medium"
                      className={styles.profileNum}
                    >
                      {data?.client.health_card}
                    </Text>
                  </div>
                  <span className={styles.buttonsRight}>
                    <div className={styles.buttonUSD}>
                      <Button
                        secondary
                        onClick={() => setOpenUploadModal(true)}
                      >
                        Upload Supporting Documents
                      </Button>
                      <Modal
                        isOpen={openUploadModal}
                        title="Upload Documents"
                        onClose={() => setOpenUploadModal(false)}
                      >
                        <Text>
                          Upload supporting documents for this application
                        </Text>
                      </Modal>
                    </div>
                    <Button
                      primary
                      onClick={methods.handleSubmit(handleSubmit)}
                      icon={<ArrowRight />}
                    >
                      Save Client
                    </Button>
                  </span>
                </div>
              </div>
              <div className={styles.pageContent}>
                <CareTeamSidebar
                  title="Care Team"
                  currUser={user}
                  careTeam={careTeam}
                  editable
                  onSave={handleSidebarSave}
                />
                <input type="hidden" {...methods.register("sales_reps")} />
                <input type="hidden" {...methods.register("authorizers")} />
                <ClientForm benefits={benefitsSelection === "true"} />
              </div>
            </>
          )}
        </div>
      </Layout>
    </FormProvider>
  );
};

export default EditClient;
